<div class="step-wrapper">
  <div
    i18n="
      XMSG|Suite Step Setup into
      text@@organizations.unified-suite-step-setup.stepSetupIntroText"
    class="header-text-wrapper"
  >
    Use this quick-start wizard to set up your product blueprint in the unified
    services framework and to create the minimum supplementary resources
    required for it.
    <app-help-popover
      i18n-title="
        XTIT|Suite Step Setup read more
        title@@organizations.unified-suite-step-setup.stepSetupReadMoreTitle"
      headerTitle="About this Quick Start Wizard"
      i18n-linkText="
        XLNK|Suite Step Setup read more
        link@@organizations.unified-suite-step-setup.stepSetupReadMoreLink"
      linkText="Read more"
      width="34rem"
      [body]="wizardPopover"
    ></app-help-popover>

    <ng-template #wizardPopover>
      Use this quick-start wizard as a starting point for setting up a product
      blueprint in the unified services framework. <br /><br />
      The wizard uses the information that you provide to create the minimum
      required resources to support your product blueprint, including
      ManagedService, ServiceMetadata, ServicePublishConfiguration,
      ResourceTypeDefinition, and BTPBlueprint resources. <br /><br />
      <span
        >Once the wizard has finished and generated these resources in your
        current folder, you'll need to finalize their configuration according to
        the steps in our onboarding
        <a
          fd-link
          href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/how-to-guides/provision-your-app/provision-your-saas-app-urm/#2-suite-level-onboarding-steps"
          [emphasized]="true"
          target="_blank"
          >how-to guide</a
        >.
      </span>
      <br /><br />

      TIPS:
      <ul>
        <li class="margin-bottom">
          Check out the in-app help provided in the wizard to guide you.
        </li>
        <li>
          We'll provide the documentation link again at the end of the wizard.
        </li>
      </ul>
    </ng-template>
  </div>

  <form [formGroup]="form" autocomplete="off" class="form-scroll">
    <div [formGroupName]="STEP_SETUP_FORM_GROUPS.STEP_SETUP">
      <div [formGroupName]="STEP_SETUP_FORM_GROUPS.SERVICE_METADATA">
        <h3
          class="section-title"
          i18n="
            XGRP|Step Setup Basic Info
            title@@organizations.unified-suite-step-setup.stepSetupMetadataTitle"
        >
          Metadata
        </h3>
        <app-container
          [body]="metadataContainer"
          class="margin-bottom"
        ></app-container>
        <ng-template #metadataContainer>
          <div class="form-wrapper">
            <app-reactive-input
              i18-label="XFLD|Suite Step Setup Name label@@organizations.unified-suite-step-setup.stepSetupNameLabel"
              label="Name"
              [formControlName]="
                STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.SUITE_NAME
              "
              popoverTitle="Name"
              popoverWidth="34rem"
              [popoverDescription]="nameInfoHelp"
              placeholder="Enter the technical name of the product blueprint"
            >
            </app-reactive-input>
            <ng-template
              #nameInfoHelp
              i18="XMSG|Step Suite Setup App display name popover body@@organizations.unified-suite-step-setup.stepSetupNamePopoverBody"
            >
              <div>
                The technical name of the product blueprint. This will be
                displayed in the URL of your product's dashboard in SAP for Me.
                <br /><br />
                Many of the resource properties that are generated by this
                wizard are based on this technical name, so please choose an
                appropriate name that applies to your product blueprint. And
                since the length is quite restricted, you might want to use
                abbreviations. <br /><br />
                The technical name must follow these guidelines:

                <ul class="margin-bottom">
                  <li class="margin-bottom">
                    Can contain only lowercase alphabetical letters (a-z),
                    numbers (0-9), hyphens (-), and periods (.).
                  </li>
                  <li class="margin-bottom">
                    Start and end with a lowercase letter.
                  </li>
                  <li class="margin-bottom">
                    Must be unique within the current folder.
                  </li>
                  <li>Limited to 15 characters.</li>
                </ul>
                IMPORTANT: You cannot change the technical name once the product
                blueprint is created.
              </div>
            </ng-template>

            <app-reactive-input
              i18-label="XFLD|Suite Step Setup DisplayName label@@organizations.unified-suite-step-setup.stepSetupDisplayNameLabel"
              label="Display Name"
              [formControlName]="
                STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.DISPLAY_NAME
              "
              popoverTitle="Display Name"
              [popoverDescription]="appDisplayNameInfoHelp"
              placeholder="Enter the official display name of the product blueprint"
            >
            </app-reactive-input>
            <ng-template
              #appDisplayNameInfoHelp
              i18="XMSG|Step Suite Setup App display name popover body@@organizations.unified-suite-step-setup.stepSetupDisplayNamePopoverBody"
            >
              The official name of the product blueprint. Customers will see
              this name in SAP for Me. <br /><br />
              If your product name has not yet been approved by SAP Brand Voice,
              you can adjust this name later in the ServiceMetadata resource.
            </ng-template>

            <app-reactive-input
              i18-label="XFLD|Suite Step Setup Description label@@organizations.unified-suite-step-setup.stepSetupDescriptionLabel"
              label="Description"
              [formControlName]="
                STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.DESCRIPTION
              "
              popoverTitle="Description"
              [popoverDescription]="appDescriptionInfoHelp"
              placeholder="Enter a short description of the product blueprint"
            >
            </app-reactive-input>
            <ng-template
              #appDescriptionInfoHelp
              i18="XMSG|Step Suite Setup App description popover body@@organizations.unified-suite-step-setup.stepSetupDescriptionPopoverBody"
            >
              A short description of the product blueprint. Customers will see
              this description in SAP for Me. <br /><br />
              We recommend that you keep the description to 3 sentences or less.
              <br /><br />
              If you don't have this information currently, you can enter it
              later in the ServiceMetadata resource.
            </ng-template>

            <app-reactive-input
              i18-label="XFLD|Suite Step Setup Description label@@organizations.unified-suite-step-setup.stepSetupDescriptionLabel"
              label="Tenant Product Type"
              [formControlName]="
                STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.TENANT_PRODUCT_TYPE
              "
              popoverTitle="Tenant Product Type"
              [required]="false"
              [popoverDescription]="tenantProductTypeInfoHelp"
              placeholder="Enter the tenant product type of the product blueprint"
            >
            </app-reactive-input>
            <ng-template
              #tenantProductTypeInfoHelp
              i18="XMSG|Step Suite Setup App tenant product type popover body@@organizations.unified-suite-step-setup.stepSetupTenantProductTypePopoverBody"
            >
              The tenant product type (TPT) for this product blueprint.
              <br /><br />
              <b
                >Important: This is the tenant product type of the product
                blueprint, not of the app.</b
              ><br /><br />
              If you do not have this information yet, you can leave this field
              empty. The wizard will insert a placeholder in the generated
              ServiceMetadata resource, which you must replace later with the
              actual value (spec.productSuite.tenantProductType).<br /><br />
              More information in the
              <a
                fd-link
                href="https://pages.github.tools.sap/atom-cfs/atom-docs/docs/how-to-guides/provision-your-app/provision-your-saas-app-urm/#2-acquire-tenant-product-type-tpt-and-system-role-for-your-entitlement-tenant"
                >how-to guide</a
              >.
            </ng-template>

            <app-reactive-input
              i18-label="XFLD|Suite Step Setup Documentation URL label@@organizations.unified-suite-step-setup.stepSetupDocumentationUrlLabel"
              label="Documentation URL"
              [formControlName]="
                STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.DOCUMENTATION_URL
              "
              popoverTitle="Documentation URL"
              [required]="false"
              [popoverDescription]="appDocumentationUrlInfoHelp"
              placeholder="Enter the URL to the documentation of the product blueprint"
            >
            </app-reactive-input>
            <ng-template
              #appDocumentationUrlInfoHelp
              i18="XMSG|Step Suite Setup App documentation url popover body@@organizations.unified-suite-step-setup.stepSetupDocumentationUrlPopoverBody"
            >
              The URL to the product blueprint's official documentation. This
              link will be made available to users in SAP for Me in your product
              blueprint’s dashboard. <br /><br />
              We recommend that you reference documentation that covers the
              setup and configuration of the product blueprint. <br /><br />
              If you don't have this information currently, you can enter it
              later in the ServiceMetadata resource.
            </ng-template>
            <app-reactive-input
              i18-label="XFLD|Suite Step Setup Icon URL label@@organizations.unified-suite-step-setup.stepSetupIconUrlLabel"
              label="Icon URL"
              [formControlName]="
                STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.ICON_URL
              "
              popoverTitle="Icon URL"
              [required]="false"
              [popoverDescription]="appIconUrlInfoHelp"
              placeholder="Enter the URL to the icon of the product blueprint"
            >
            </app-reactive-input>
            <ng-template
              #appIconUrlInfoHelp
              i18="XMSG|Step Suite Setup App icon url popover body@@organizations.unified-suite-step-setup.stepSetupIconUrlPopoverBody"
            >
              The URL to the product blueprint’s official icon. This icon will
              be displayed in SAP for Me. <br /><br />
              The icon must be in SVG format, or you can reference an icon in
              the SAP icon library. For example: sap-icon://dimension
              <br /><br />
              If you don't yet have an icon, leave this empty. A default icon
              will be assigned. You can enter the icon URL later in the
              ServiceMetadata resource.
            </ng-template>

            <div style="display: flex; align-items: center">
              <fd-checkbox
                [formControlName]="
                  STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.PACKAGE
                "
                label="Set Up as Package"
                [fdCompact]="true"
                [required]="false"
              >
              </fd-checkbox>

              <app-help-popover
                placement="bottom"
                style="margin-top: 0.2rem"
                [body]="packageInfoHelp"
                width="34rem"
                headerTitle="Set Up as Package"
              ></app-help-popover>

              <ng-template #packageInfoHelp>
                Determines whether the setup must be labeled as a package in SAP
                for Me:

                <ul>
                  <li>
                    Select this option if your blueprint includes more than one
                    leading application (where the application is defined with
                    createMode "Optional"). In SAP for Me, the Applications tab
                    will list multiple applications for customers to provision
                    as part of the solution. Users in SAP for Me, will also see
                    this product blueprint marked with a label called 'Package'.
                  </li>
                  <br />

                  <li>
                    Deselect this option if your setup includes only single or
                    standalone application/tenant. In the SAP for Me user
                    interface, users will not see the product blueprint labeled
                    as a package.
                  </li>
                </ul>
              </ng-template>
            </div>

            <div style="display: flex; flex-direction: column; gap: 0">
              <app-helpful-label
                for="serviceMetadataCategory"
                label="Category"
                popoverTitle="Category"
                [popoverBody]="categoryInfoHelp"
              >
              </app-helpful-label>

              <fd-select
                id="serviceMetadataCategory"
                fdCompact
                [required]="true"
                [formControlName]="
                  STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.CATEGORY
                "
                fillControlMode="fit-content"
                [inline]="false"
              >
                @for (c of categoryOptions(); track c) {
                  <fd-option [value]="c.key">
                    {{ c.label }}
                  </fd-option>
                }
              </fd-select>

              <ng-template #categoryInfoHelp>
                Select the official SAP portfolio category used to list this
                product blueprint in SAP for Me, according to the closed list of
                classifications on
                <a
                  fd-link
                  [emphasized]="true"
                  href="https://www.sap.com/products.html"
                  target="_blank"
                  >www.sap.com</a
                >.
                <br />
                <br />
                If no categories match your product blueprint, please choose
                <strong>Other</strong>.
              </ng-template>
            </div>
          </div>
        </ng-template>
      </div>
      <div class="title-wrapper">
        <h3
          class="section-title"
          i18n="
            XGRP|Step Setup Business Types
            title@@organizations.unified-suite-step-setup.stepSetupBusinessTypesTitle"
        >
          Business Types
        </h3>
        <button
          fd-button
          (click)="addBusinessType()"
          title="Add Business Type"
          [fdCompact]="true"
          fdType="emphasized"
        >
          <app-icon icon="plus"></app-icon>
        </button>
      </div>

      <div
        [formArrayName]="STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPES"
        class="form-wrapper"
      >
        @for (
          businessType of businessTypes.controls;
          track businessType;
          let i = $index
        ) {
          <div [formGroupName]="i">
            <app-container
              [body]="businessTypeContainer"
              class="margin-bottom"
            ></app-container>
            <ng-template #businessTypeContainer>
              <div class="business-type-container">
                <button
                  fd-button
                  class="remove-button"
                  (click)="removeBusinessType(i)"
                  [title]="
                    businessTypes.controls.length === 1
                      ? 'Cannot delete this; you must have at least one business type'
                      : 'Remove Business Type'
                  "
                  [disabled]="businessTypes.controls.length === 1"
                  [fdCompact]="true"
                  fdType="transparent"
                >
                  <app-icon icon="trash"></app-icon>
                </button>
                <div class="form-wrapper">
                  <fd-form-input-message-group
                    [triggers]="['mouseenter', 'mouseleave']"
                  >
                    <div class="business-type-header">
                      <app-helpful-label
                        [required]="true"
                        for="businessTypeType"
                        i18-label="XFLD|Step Setup BusinessType Type label@@organizations.unified-suite-step-setup.stepSetupBusinessTypeTypeLabel"
                        label="Business Type"
                        [popoverBody]="type"
                        i18-popoverTitle="XTIT|Step Setup BusinessType Type popover title@@organizations.unified-suite-step-setup.stepSetupBusinessTypeTypePopoverTitle"
                        popoverTitle="Business Type"
                      >
                        <ng-template
                          #type
                          i18="XMSG|Step Setup BusinessType Type popover body@@organizations.unified-suite-step-setup.stepSetupBusinessTypeTypePopoverBody"
                        >
                          Choose one of the 3 common business types or
                          <strong>Other</strong> to add an alternative business
                          type.<br /><br />
                          IMPORTANT: If you choose <strong>Other</strong>, you
                          must manually fill in the business type's title and
                          code, as defined in the SAP Cloud Landscape Directory
                          (CLD). For more information, refer to the field help
                          of the <strong>Title</strong> and
                          <strong>Code</strong> fields.
                        </ng-template>
                      </app-helpful-label>
                    </div>
                    <fd-select
                      fdCompact
                      [required]="true"
                      id="businessTypeType"
                      [formControlName]="
                        STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPE.TYPE
                      "
                      placeholder="Choose a business type"
                      fillControlMode="fit-content"
                      [inline]="false"
                    >
                      @for (option of optionSets()[i]; track option) {
                        <fd-option [value]="option.value">{{
                          option.key
                        }}</fd-option>
                      }
                    </fd-select>
                  </fd-form-input-message-group>
                  <app-reactive-input
                    i18-label="XFLD|Suite Step Setup BusinessType Title label@@organizations.unified-suite-step-setup.stepSetupBusinessTypeTitleLabel"
                    label="Title"
                    [formControlName]="
                      STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPE.TITLE
                    "
                    popoverTitle="Title"
                    [popoverDescription]="titleInfoHelp"
                    placeholder="Enter the display title of the business type"
                  >
                  </app-reactive-input>
                  <ng-template
                    #titleInfoHelp
                    i18="XMSG|Step Suite Setup business type title popover body@@organizations.unified-suite-step-setup.stepSetupBusinessTypeTitlePopoverBody"
                  >
                    Title of the business type as defined in the SAP Cloud
                    Landscape Directory (CLD). Go to the
                    <a
                      fd-link
                      href="https://wiki.one.int.sap/wiki/x/PYqQc#BusinessTypesforSystemsandTenants-TenantBusinessTypes"
                      [emphasized]="true"
                      target="_blank"
                      >CLD wiki</a
                    >, and in the
                    <strong>Tenant Business Types</strong> section, look under
                    the <strong>External / Customer Facing Name</strong> column.
                    <br /><br />
                    Example: Production
                    <br /><br />
                    NOTE: There is no validation of the value you enter, so make
                    sure you enter it correctly to prevent downstream errors.
                  </ng-template>
                  <app-reactive-input
                    i18-label="XFLD|Suite Step Setup BusinessType Code label@@organizations.unified-suite-step-setup.stepSetupBusinessTypeCodeLabel"
                    label="Code"
                    [formControlName]="
                      STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPE.CODE
                    "
                    popoverTitle="Code"
                    [popoverDescription]="codeInfoHelp"
                    placeholder="Enter the code of the business type"
                  >
                  </app-reactive-input>
                  <ng-template
                    #codeInfoHelp
                    i18="XMSG|Step Suite Setup business type code popover body@@organizations.unified-suite-step-setup.stepSetupBusinessTypeCodePopoverBody"
                  >
                    Code of the business type as defined in the SAP Cloud
                    Landscape Directory (CLD). Go to the
                    <a
                      fd-link
                      href="https://wiki.one.int.sap/wiki/x/PYqQc#BusinessTypesforSystemsandTenants-TenantBusinessTypes"
                      [emphasized]="true"
                      target="_blank"
                      >CLD wiki</a
                    >, and in the
                    <strong>Tenant Business Types</strong> section, look under
                    the <strong>Code</strong> column. <br /><br />
                    Example: ZH421
                    <br /><br />
                    NOTE: We perform a basic formatting check on the code you
                    enter. However, there is no verification that the code is
                    valid or if it matches the business type name. Please ensure
                    you enter it correctly to prevent downstream errors.
                  </ng-template>
                </div>
              </div>
            </ng-template>
          </div>
        }
      </div>
    </div>
  </form>
</div>
